'use client';

import { unstable_composeClasses as composeClasses } from '@mui/base/composeClasses';
import { useButton } from '@mui/base/useButton';
import ButtonGroupContext from '@mui/joy/ButtonGroup/ButtonGroupContext';
import CircularProgress from '@mui/joy/CircularProgress';
import ToggleButtonGroupContext from '@mui/joy/ToggleButtonGroup/ToggleButtonGroupContext';
import { unstable_capitalize as capitalize, unstable_useForkRef as useForkRef } from '@mui/utils';
import * as React from 'react';
import type {
  ExtendIconButton,
  IconButtonOwnerState,
  IconButtonTypeMap,
} from './IconButtonBaseProps';
import { getIconButtonUtilityClass } from './iconButtonBaseClasses';

const useUtilityClasses = (ownerState: IconButtonOwnerState) => {
  const { color, disabled, focusVisible, focusVisibleClassName, size, variant, loading } =
    ownerState;

  const slots = {
    root: [
      'root',
      disabled && 'disabled',
      focusVisible && 'focusVisible',
      variant && `variant${capitalize(variant)}`,
      color && `color${capitalize(color)}`,
      size && `size${capitalize(size)}`,
      loading && 'loading',
    ],
    loadingIndicator: ['loadingIndicator'],
  };

  const composedClasses = composeClasses(slots, getIconButtonUtilityClass, {});

  if (focusVisible && focusVisibleClassName) {
    composedClasses.root += ` ${focusVisibleClassName}`;
  }

  return composedClasses;
};

function resolveSlotProps(
  slotPropValue: unknown,
  ownerState: IconButtonOwnerState,
): Record<string, unknown> | undefined {
  return typeof slotPropValue === 'function'
    ? slotPropValue(ownerState)
    : (slotPropValue as Record<string, unknown> | undefined);
}

const IconButtonBase = React.forwardRef(function IconButtonBase(
  // oxlint-disable-next-line no-explicit-any -- polymorphic component accepts any props via OverridableComponent
  inProps: any,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const {
    children,
    action,
    color: colorProp = 'neutral',
    variant: variantProp = 'plain',
    size: sizeProp = 'md',
    loading = false,
    loadingIndicator: loadingIndicatorProp,
    disabled: disabledProp,
    component,
    slots = {},
    slotProps = {},
    className: classNameProp,
    style,
    // keep these off the DOM: sx/focusVisibleClassName are Joy-only; as is the polymorphic alias
    sx: _sx,
    focusVisibleClassName: _focusVisibleClassName,
    as,
    ...other
  } = inProps;

  const buttonGroup = React.useContext(ButtonGroupContext);
  const toggleButtonGroup = React.useContext(ToggleButtonGroupContext);

  const variant = inProps.variant || buttonGroup.variant || variantProp;
  const size = inProps.size || buttonGroup.size || sizeProp;
  const color = inProps.color || buttonGroup.color || colorProp;
  // verbatim port of Joy's disabled logic (IconButton.js): the ?? only fires when both
  // inProps.loading/disabled are omitted — explicit false skips the buttonGroup fallback
  const disabled =
    (inProps.loading || inProps.disabled) ?? (buttonGroup.disabled || loading || disabledProp);

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const handleRef = useForkRef(buttonRef, ref);

  const { focusVisible, setFocusVisible, getRootProps } = useButton({
    ...inProps,
    disabled,
    rootRef: handleRef,
  });

  const loadingIndicator = loadingIndicatorProp ?? (
    <CircularProgress
      color={color}
      thickness={({ xs: 2, sm: 2, md: 3, lg: 4 } as Record<string, number>)[size] || 3}
    />
  );

  React.useImperativeHandle(
    action,
    () => ({
      focusVisible: () => {
        setFocusVisible(true);
        buttonRef.current?.focus();
      },
    }),
    [setFocusVisible],
  );

  const ownerState: IconButtonOwnerState = {
    ...inProps,
    color,
    variant,
    size,
    focusVisible,
    loading,
    disabled,
    instanceSize: inProps.size,
  };

  const classes = useUtilityClasses(ownerState);

  const rootSlotProps = resolveSlotProps(slotProps.root, ownerState);
  const loadingIndicatorSlotProps = resolveSlotProps(slotProps.loadingIndicator, ownerState);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const onClick = rootSlotProps?.onClick ?? inProps.onClick;
    onClick?.(event);
    if (toggleButtonGroup) {
      toggleButtonGroup.onClick?.(event, inProps.value);
    }
  };

  let ariaPressed = rootSlotProps?.['aria-pressed'] ?? inProps['aria-pressed'];
  if (toggleButtonGroup?.value !== undefined && toggleButtonGroup?.value !== null) {
    if (Array.isArray(toggleButtonGroup.value)) {
      ariaPressed = toggleButtonGroup.value.indexOf(inProps.value) !== -1;
    } else {
      ariaPressed = toggleButtonGroup.value === inProps.value;
    }
  }

  const RootSlot = slots.root || component || as || 'button';
  const LoadingIndicatorSlot = slots.loadingIndicator || 'span';

  // merge rootSlotProps but exclude keys we handle explicitly to avoid duplication
  const {
    className: rootSlotClassName,
    style: rootSlotStyle,
    onClick: _rootOnClick,
    'aria-pressed': _rootAriaPressed,
    ...restRootSlotProps
  } = rootSlotProps ?? {};

  const rootProps = getRootProps({
    ...other,
    ...restRootSlotProps,
    onClick: handleClick,
    'aria-pressed': ariaPressed,
    className: [classes.root, classNameProp, rootSlotClassName].filter(Boolean).join(' '),
    style: { ...style, ...(rootSlotStyle as React.CSSProperties) },
  });

  return (
    <RootSlot {...rootProps}>
      {loading ? (
        <LoadingIndicatorSlot
          {...loadingIndicatorSlotProps}
          className={[classes.loadingIndicator, loadingIndicatorSlotProps?.className]
            .filter(Boolean)
            .join(' ')}
        >
          {loadingIndicator}
        </LoadingIndicatorSlot>
      ) : (
        children
      )}
    </RootSlot>
  );
}) as ExtendIconButton<IconButtonTypeMap>;

// @ts-expect-error -- mirrors Joy's own IconButton.muiName convention
IconButtonBase.muiName = 'IconButton';

export default IconButtonBase;
